import { useEffect, useRef, useState } from "react";

/**
 * Multi-select combobox for a client's FINTRAC report types.
 *
 * These four are the report types the pipeline actually builds (see
 * ecs/report_generator/builders/). CDR is deliberately absent: its builder is
 * a stub with no casino data model behind it, so offering it here would
 * configure a client for reports that can never be generated.
 */
export const REPORT_TYPES: Array<{ code: string; name: string; description: string }> = [
  {
    code: "EFTR",
    name: "Electronic Funds Transfer Report",
    description: "International electronic funds transfers of CAD 10,000 or more.",
  },
  {
    code: "LCTR",
    name: "Large Cash Transaction Report",
    description: "Cash received totalling CAD 10,000 or more in 24 hours.",
  },
  {
    code: "LVCTR",
    name: "Large Virtual Currency Transaction Report",
    description: "Virtual currency received worth CAD 10,000 or more in 24 hours.",
  },
  {
    code: "STR",
    name: "Suspicious Transaction Report",
    description: "Reasonable grounds to suspect money laundering or terrorist financing. No threshold.",
  },
];

interface Props {
  id: string;
  value: string[];
  onChange: (next: string[]) => void;
  invalid?: boolean;
}

export function ReportTypeSelect({ id, value, onChange, invalid }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  /* Toggling keeps the menu open -- picking several types is the normal case.
     Selection order is normalised to REPORT_TYPES order so the value does not
     depend on which checkbox the operator happened to click first. */
  const toggle = (code: string) => {
    const next = value.includes(code) ? value.filter((c) => c !== code) : [...value, code];
    onChange(REPORT_TYPES.map((t) => t.code).filter((c) => next.includes(c)));
  };

  return (
    <div className="multiselect" ref={ref}>
      <button
        type="button"
        id={id}
        className="ms-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid ? true : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={value.length ? "ms-value" : "ms-placeholder"}>
          {value.length ? value.join(", ") : "Select report types"}
        </span>
        <span className="caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="ms-menu" role="listbox" aria-multiselectable="true" aria-labelledby={id}>
          {REPORT_TYPES.map((t) => {
            const selected = value.includes(t.code);
            return (
              <button
                type="button"
                key={t.code}
                role="option"
                aria-selected={selected}
                className={`ms-option${selected ? " selected" : ""}`}
                onClick={() => toggle(t.code)}
              >
                <span className="tick" aria-hidden="true">{selected ? "✓" : ""}</span>
                <span className="ms-option-text">
                  <span className="ms-option-title">
                    <strong>{t.code}</strong> — {t.name}
                  </span>
                  <span className="ms-option-desc">{t.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
